import { createRoot } from 'react-dom/client';

import Archive from '@spectrum-icons/workflow/Archive';
import Email from '@spectrum-icons/workflow/Email';
import FolderSearch from '@spectrum-icons/workflow/FolderSearch';
import Home from '@spectrum-icons/workflow/Home';
import Homepage from '@spectrum-icons/workflow/Homepage';
import InfoOutline from '@spectrum-icons/workflow/InfoOutline';
import Mailbox from '@spectrum-icons/workflow/Mailbox';
import OpenIn from '@spectrum-icons/workflow/OpenIn';
import RailLeft from '@spectrum-icons/workflow/RailLeft';
import Refresh from '@spectrum-icons/workflow/Refresh';
import Settings from '@spectrum-icons/workflow/Settings';
import TaskList from '@spectrum-icons/workflow/TaskList';
import User from '@spectrum-icons/workflow/User';


import { faBook, faCheckDouble, faFile, faFileAlt, faFileCsv, faFileExcel, faFilePdf, faFilePowerpoint, faFileWord, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { TracingInterceptor } from '@beso-rsd/beso-rsd-tracing';
import { isElectron } from '@baeso-ui/baeso-core';

import { SpectrumFontAwesomeIcon, registerIcon } from '@baeso-ui/baeso-ui-spectrum';

import './styles/fonts.css';
import './styles/theme.css';
import './index.css';

import App from './App';

import * as serviceWorker from './serviceWorker';

registerIcon('fa-home-lg-alt-solid', <Home size="M" />);
registerIcon('fa-file-search', <FolderSearch size="M" />);
registerIcon('fa-tasks', size => <TaskList size={size} />);
registerIcon('fa-mail-bulk', <Mailbox size="M" />);
registerIcon('fa-archive', <Archive />);
registerIcon('about-icon', <InfoOutline size="L" />);
registerIcon('pref-icon', <Settings size="L" />);

registerIcon('person', size => <User size={size} />);
registerIcon('nachricht', size => <Email size={size} />);
registerIcon('akte', size => <SpectrumFontAwesomeIcon icon={faBook} size={size} />);

registerIcon('home', size => <Home size={size} />);
registerIcon('homepage', size => <Homepage size={size} />);
registerIcon('archive', size => <Archive size={size} />);
registerIcon('user', size => <User size={size} />);
registerIcon('refresh', size => <Refresh size={size} />);
registerIcon('double-check', size => <SpectrumFontAwesomeIcon icon={faCheckDouble} size={size} />);
registerIcon('plus', size => <SpectrumFontAwesomeIcon icon={faPlus} size={size} />);
registerIcon('trash', size => <SpectrumFontAwesomeIcon icon={faTrash} size={size} />);
registerIcon('show-navigation-left', size => <RailLeft size={size} />);
registerIcon('open-external', size => <OpenIn size={size} />);

registerIcon('document-generic', size => <SpectrumFontAwesomeIcon icon={faFile} size={size} />);
registerIcon('document-word', size => <SpectrumFontAwesomeIcon icon={faFileWord} size={size} />);
registerIcon('document-cell', size => <SpectrumFontAwesomeIcon icon={faFileExcel} size={size} />);
registerIcon('document-slide', size => <SpectrumFontAwesomeIcon icon={faFilePowerpoint} size={size} />);
registerIcon('document-pdf', size => <SpectrumFontAwesomeIcon icon={faFilePdf} size={size} />);
registerIcon('document-text', size => <SpectrumFontAwesomeIcon icon={faFileAlt} size={size} />);
registerIcon('document-csv', size => <SpectrumFontAwesomeIcon icon={faFileCsv} size={size} />);

// load OnlyOffice API script from Document Server
// loading dynamically (instead of static reference in index.html) to be able to check for an Electron environment and 
// build the appropriate full URL if needed
(() => {
  const script = document.createElement('script');
  let docserverHost;
  if (process.env.REACT_APP_ONLYOFFICE_DOCUMENT_SERVER) {
    docserverHost = process.env.REACT_APP_ONLYOFFICE_DOCUMENT_SERVER;
  } else {
    const baseURL = new URL(isElectron() && process.env.REACT_APP_SERVICE_ELECTRON_BASEURL ? process.env.REACT_APP_SERVICE_ELECTRON_BASEURL : window.location.origin); 
    // append "-onlyoffice" to the subdomain
    const appHostnameParts = baseURL.hostname.split('.');
    appHostnameParts[0] += '-onlyoffice';
    baseURL.hostname = appHostnameParts.join('.');
    docserverHost = baseURL.toString();
  }
  script.src = [ docserverHost, '/web-apps/apps/api/documents/api.js' ].join('');
  script.async = true;
  document.body.appendChild(script);
})();

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- 'root' element must exist in the (static) HTML template
createRoot(document.getElementById('root')!).render(<App />);

const trace = new TracingInterceptor( {
  serviceName: 'onacta',
  url: isElectron() ? process.env.REACT_APP_SERVICE_ELECTRON_BASEURL + '/trace/api/v2/spans' : '/trace/api/v2/spans',
});
trace.enable();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
